import { useEffect, useRef } from 'react'

import type { RecipeQueryResponse } from '@library/api-menu-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

import type { LoadPromotionBannerEvent } from 'routes/Menu/MenuExperience/types'

type UseRecipeDetailsPromotionTrackingArgs = {
  menuId: string
  recipe: RecipeQueryResponse | null
}

// The offer header is the text the customer sees on the recipe image, for example
// "OFFER | Save £3 on 3 portions"
const getPromotionText = (recipe: RecipeQueryResponse) =>
  (recipe.tagGroups.header?.tags ?? [])
    .map(({ text }) => text)
    .filter(Boolean)
    .join(' ')

// view_recipe_details fires when the details open, before the recipe has loaded, so the offer
// is reported separately once it has. It fires again for each recipe that loads, such as a
// variant picked inside the details, and not at all for a recipe without an offer
export const useRecipeDetailsPromotionTracking = ({
  menuId,
  recipe,
}: UseRecipeDetailsPromotionTrackingArgs) => {
  const trackedRecipeIdRef = useRef<string | null>(null)
  const promotionId = recipe?.analytics?.promotionId
  const recipeId = recipe?.id
  const promotionText = recipe && getPromotionText(recipe)

  useEffect(() => {
    if (!recipeId) {
      return
    }

    // A recipe without an offer is the one on show now, so going back to the offer recipe
    // before it is a new load to report
    if (!promotionId) {
      trackedRecipeIdRef.current = null

      return
    }

    if (trackedRecipeIdRef.current === recipeId) {
      return
    }

    queueTrackingEvent<LoadPromotionBannerEvent>({
      action: 'load_promotion_banner',
      properties: {
        action: 'load',
        event_screen: 'recipe_details',
        menu_uuid: menuId,
        promotion_id: promotionId,
        ...(promotionText && { promotion_text: promotionText }),
      },
    })

    trackedRecipeIdRef.current = recipeId
  }, [menuId, promotionId, promotionText, recipeId])
}
